import getHashString from 'bookingsv2-utils/lib/analytics/getHashString';
import type { InboxReminder } from 'owa-graph-schema';
import { logUsage } from 'owa-analytics';
import {
    DEFAULT_REMINDER_ID,
    DEFAULT_FOLLOW_UP_ID,
} from 'bookingsv2-constants/lib/reminders-notifications';
import getDefaultFollowUpMessage from './getDefaultFollowUpMessage';
import getReminderFollowUpCount from 'bookingsv2-utils/lib/getReminderFollowUpCount';

const logDefaultFollowUpTelemetry = (reminder: InboxReminder, hashedServiceId: string) => {
    logUsage('BookingsV2_DefaultFollowUpSendTo', {
        dynamicEventName: `DefaultFollowUpSendTo${reminder.SendOption}`,
        ServiceId: hashedServiceId,
    });
    logUsage('DefaultFollowUpTiming', {
        Offset: reminder.ReminderOffset,
        ServiceId: hashedServiceId,
    });
    if (reminder.RichTextMessage !== getDefaultFollowUpMessage()) {
        logUsage('DefaultFollowUpMessageEdited');
    }
};

const logDefaultReminderTelemetry = (reminder: InboxReminder, hashedServiceId: string) => {
    logUsage('BookingsV2_DefaultReminderSendTo', {
        dynamicEventName: `DefaultReminderSendTo${reminder.SendOption}`,
        ServiceId: hashedServiceId,
    });
    logUsage('DefaultReminderTiming', {
        Offset: reminder.ReminderOffset,
        ServiceId: hashedServiceId,
    });
};

const logReminderFollowUpTelemetry = (
    inboxReminders: InboxReminder[],
    serviceId: string,
    isEdit: boolean,
    iInboxReminders?: InboxReminder[]
) => {
    const hashedServiceId = getHashString(serviceId);

    // Getting no. of reminders/follow-ups being added for the service
    const { inboxReminderCount, inboxFollowUpCount } = getReminderFollowUpCount(inboxReminders);

    //For edit service scenario
    if (isEdit) {
        logUsage('BookingsServiceEdited', { ServiceId: hashedServiceId });

        // Getting no. of reminders/follow-ups before service was edited
        const iReminderFollowUpCount = getReminderFollowUpCount(iInboxReminders ?? []);

        // Logging below datapoints if user added/deleted follow-ups while editing the service
        const followUpCountDiff = iReminderFollowUpCount.inboxFollowUpCount - inboxFollowUpCount;
        if (followUpCountDiff < 0) {
            logUsage('ServiceEditFollowUpAdded', {
                Total: inboxFollowUpCount,
                Count: -followUpCountDiff,
                ServiceId: hashedServiceId,
            });
        } else if (followUpCountDiff > 0) {
            logUsage('ServiceEditFollowUpDeleted', {
                Total: inboxFollowUpCount,
                Count: followUpCountDiff,
                ServiceId: hashedServiceId,
            });
        }

        // Logging below datapoints if user added/deleted reminders while editing the service
        const reminderCountDiff = iReminderFollowUpCount.inboxReminderCount - inboxReminderCount;
        if (reminderCountDiff < 0) {
            logUsage('ServiceEditReminderAdded', {
                Total: inboxReminderCount,
                Count: -reminderCountDiff,
                ServiceId: hashedServiceId,
            });
        } else if (reminderCountDiff > 0) {
            logUsage('ServiceEditReminderDeleted', {
                Total: inboxReminderCount,
                Count: reminderCountDiff,
                ServiceId: hashedServiceId,
            });
        }
    } else {
        //For create service scenario
        logUsage('BookingsServiceAdded', { ServiceId: hashedServiceId });

        logUsage('ServiceEmailFollowUpCount', {
            Count: inboxFollowUpCount,
            ServiceId: hashedServiceId,
        });
        logUsage('ServiceEmailReminderCount', {
            Count: inboxReminderCount,
            ServiceId: hashedServiceId,
        });

        let isDefaultFollowUpDeleted = true;
        let isDefaultReminderDeleted = true;

        inboxReminders.map(reminder => {
            // Checking if it's a follow-up
            if (Number(reminder.ReminderOffset) < 0) {
                // Checking if it's a default follow-up
                if (reminder.Id === DEFAULT_FOLLOW_UP_ID) {
                    isDefaultFollowUpDeleted = false;
                    logDefaultFollowUpTelemetry(reminder, hashedServiceId);
                } else {
                    /* eslint-disable-next-line owa-custom-rules/forbid-specific-functions-patterns-inside-loops -- (https://aka.ms/OWALintWiki)
                     * Baseline, this function can't be used inside a loop, please move it outside
                     *	> Function 'logUsage' matches forbidden pattern (/logUsage/) and should not be used inside loops */
                    logUsage('BookingsV2_FollowUpSendTo', {
                        dynamicEventName: `FollowUpSendTo${reminder.SendOption}`,
                        ServiceId: hashedServiceId,
                    });
                    /* eslint-disable-next-line owa-custom-rules/forbid-specific-functions-patterns-inside-loops -- (https://aka.ms/OWALintWiki)
                     * Baseline, this function can't be used inside a loop, please move it outside
                     *	> Function 'logUsage' matches forbidden pattern (/logUsage/) and should not be used inside loops */
                    logUsage('FollowUpTiming', {
                        Offset: reminder.ReminderOffset,
                        ServiceId: hashedServiceId,
                    });
                }
            } else {
                // Checking if it's a default reminder
                if (reminder.Id === DEFAULT_REMINDER_ID) {
                    isDefaultReminderDeleted = false;
                    logDefaultReminderTelemetry(reminder, hashedServiceId);
                } else {
                    /* eslint-disable-next-line owa-custom-rules/forbid-specific-functions-patterns-inside-loops -- (https://aka.ms/OWALintWiki)
                     * Baseline, this function can't be used inside a loop, please move it outside
                     *	> Function 'logUsage' matches forbidden pattern (/logUsage/) and should not be used inside loops */
                    logUsage('BookingsV2_ReminderSendTo', {
                        dynamicEventName: `ReminderSendTo${reminder.SendOption}`,
                        ServiceId: hashedServiceId,
                    });
                    /* eslint-disable-next-line owa-custom-rules/forbid-specific-functions-patterns-inside-loops -- (https://aka.ms/OWALintWiki)
                     * Baseline, this function can't be used inside a loop, please move it outside
                     *	> Function 'logUsage' matches forbidden pattern (/logUsage/) and should not be used inside loops */
                    logUsage('ReminderTiming', {
                        Offset: reminder.ReminderOffset,
                        ServiceId: hashedServiceId,
                    });
                }
            }
        });
        if (isDefaultFollowUpDeleted) {
            logUsage('DefaultFollowUpDeleted');
        }
        if (isDefaultReminderDeleted) {
            logUsage('DefaultReminderDeleted');
        }
    }
};

export default logReminderFollowUpTelemetry;
